import Infotip from 'APP/Converse/Inputs/Infotip'
import Typography from 'APP/Converse/Typography'
import { isIOS } from 'APP/Helpers/checkPlatform'
import escapeRegExp from 'lodash/escapeRegExp'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ActivityIndicator,
  FlatList,
  Keyboard,
  Modal,
  NativeSyntheticEvent,
  Platform,
  TextInput,
  TextInputKeyPressEventData,
  TouchableHighlight,
  TouchableWithoutFeedback,
  useWindowDimensions,
  View,
  ViewStyle,
  Text,
} from 'react-native'
import Animated, {
  interpolate,
  interpolateColor,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  type AnimatedStyle,
} from 'react-native-reanimated'
import { useTheme } from 'styled-components/native'
import { AutocompleteContainerView } from './AutocompleteContainerView'
import { AutocompleteInputProvider, useAutocompleteInputContext } from './AutocompleteInputProvider'
import { AutocompleteInputScrollView, wrapScrollView } from './AutocompleteInputScrollView'
import {
  DROPDOWN_HEIGHT,
  INPUT_HEIGHT,
  StyledActivityIndicatorContainer,
  StyledAutocompleteContainer,
  StyledCaption,
  StyledDropdown,
  StyledDropdownContainer,
  StyledDropdownContent,
  StyledIconContainer,
  StyledInput,
  StyledInputContainer,
  StyledOption,
  StyledValueContainer,
  StyledValueLabel,
} from './styles'

const Label = ({
  label,
  showRequiredAsterisk,
  style,
}: {
  label: string
  showRequiredAsterisk?: boolean
  style?: AnimatedStyle<ViewStyle>
}) => (
  <Animated.View style={[{ flexDirection: 'row', marginBottom: 4 }, style]}>
    <Typography variant="bodyNormalBold" testID="PICKER_LABEL">
      {label}
    </Typography>
    {showRequiredAsterisk && (
      <Typography variant="bodyNormalBold" color="error" testID="PICKER_LABEL_ASTERISK">
        &nbsp;*
      </Typography>
    )}
  </Animated.View>
)

const HighlightedText = ({ text, searchTerm }: { text: string; searchTerm: string }) => {
  if (!searchTerm) return <Typography variant="bodyNormal">{text}</Typography>

  const regex = new RegExp(`(${escapeRegExp(searchTerm)})`, 'gi')
  const parts = text.split(regex)

  return (
    <Typography variant="bodyNormal" numberOfLines={1}>
      {parts.map((part, index) =>
        regex.test(part) ? (
          <Typography key={index} variant="bodyNormalBold">
            {part}
          </Typography>
        ) : (
          part
        )
      )}
    </Typography>
  )
}
export interface AutocompleteOption {
  value: string
  label: string
  caption?: string
  emoji?: string
}

interface AutocompleteInputProps<T extends AutocompleteOption> {
  error?: string
  infotip?: string
  isPersistentInfotip?: boolean
  label?: string | null
  /**
   * Load options based on the input value. This function can be debounced using `debounceTime` prop. The `signal`
   * parameter can be passed to network requests to allow cancellation of previous requests.
   */
  loadOptions: (inputValue: string, { signal }: { signal: AbortSignal }) => T[] | Promise<T[]>
  /**
   * The selected option.
   */
  option: T | null
  /**
   * Called when the selected option changes.
   */
  onChange: (option: T | null) => void
  placeholder?: string
  showRequiredAsterisk?: boolean
  testID?: string
  icon?: React.ReactNode
  /**
   * Optional debounce time in milliseconds for input changes.
   */
  debounceTime?: number
  maxLength?: number
  readyToOpen?: boolean
}

const keyExtractor = (item: AutocompleteOption) => item.value

const AutocompleteInput = <T extends AutocompleteOption>({
  error,
  infotip,
  isPersistentInfotip,
  label,
  loadOptions,
  onChange,
  placeholder,
  testID,
  option,
  showRequiredAsterisk,
  icon,
  debounceTime = 0,
  maxLength,
  readyToOpen = true,
}: AutocompleteInputProps<T>) => {
  const theme = useTheme()
  const {
    setIsDropdownOpened: setContextIsDropdownOpened,
    screenTop,
    hasContext,
  } = useAutocompleteInputContext()
  const [focused, setFocused] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [openRequested, setOpenRequested] = useState(false)
  const focusedAnim = useSharedValue(0)
  const [input, setInput] = useState('')
  const [options, setOptions] = useState<T[]>([])
  const [loading, setLoading] = useState(false)
  const [transitioning, setTransitioning] = useState(false)
  const [position, setPosition] = useState<{
    x: number
    y: number
    width: number
    height: number
  } | null>(null)
  const positionY = position?.y ?? 0
  // `screenTop + 10` preserves the legacy spacing. With Fabric, iOS measures inside the
  // page-sheet while the dropdown renders full-screen, so clamp it to the safe area.
  // TODO: Render both elements in the same modal coordinate system.
  // Context: https://github.com/dialoguemd/member-app/pull/7706#discussion_r3915453500
  const minimumDropdownTop = theme.insets.top + theme.metrics.baseMargin
  const dropdownTargetTop = isIOS()
    ? Math.max((screenTop ?? 0) + 10, minimumDropdownTop)
    : (screenTop ?? 0) + 10
  const containerRef = useRef<View>(null)
  const inputRef = useRef<TextInput>(null)
  const lastRequestId = useRef(0)
  const measureRequestId = useRef(0)
  const pendingModalRelease = useRef(false)
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1)
  const window = useWindowDimensions()

  // Handle loading options with debounce.
  useEffect(() => {
    if (!focused) return

    let abortController: AbortController | null = null
    const timeoutId = setTimeout(() => {
      const execute = async () => {
        lastRequestId.current += 1
        const requestId = lastRequestId.current

        setLoading(true)

        try {
          // Pass an abort signal to the loadOptions function to allow cancellation of
          // previous requests.
          abortController = new AbortController()
          const filteredOptions = await loadOptions(input, { signal: abortController.signal })
          abortController = null
          if (requestId === lastRequestId.current) {
            setOptions(filteredOptions)
          }
        } finally {
          if (requestId === lastRequestId.current) {
            setLoading(false)
          }
        }
      }

      execute()
    }, debounceTime)

    return () => {
      clearTimeout(timeoutId)
      abortController?.abort()
    }
  }, [input, focused, debounceTime, loadOptions])

  useEffect(() => {
    const stopTransitioning = () => {
      setTransitioning(false)
      if (!focused && !isIOS()) {
        setModalVisible(false)
      }
    }

    // On iOS, removing the modal in the same turn as blur leaves a keyboard-window
    // copy of the field. Release happens from onBlur instead.
    if (!focused && !isIOS()) {
      setModalVisible(false)
      setPosition(null)
    }

    focusedAnim.value = withTiming(focused ? 1 : 0, { duration: 150 }, (finished) => {
      if (finished) {
        runOnJS(stopTransitioning)()
      }
    })
  }, [focused, focusedAnim])

  const releaseModal = useCallback(() => {
    pendingModalRelease.current = false
    setModalVisible(false)
    setPosition(null)
  }, [])

  const closeDropdown = useCallback(() => {
    measureRequestId.current += 1
    setOpenRequested(false)
    setFocused(false)
    setContextIsDropdownOpened(false)
    setTransitioning(false)

    const input = inputRef.current
    if (isIOS() && input?.isFocused?.()) {
      // Keyboard.dismiss() detaches a focused modal input into the iOS keyboard
      // window. Blur it, then unmount only after it resigns first responder.
      pendingModalRelease.current = true
      input.blur()
      return
    }

    if (!isIOS()) {
      Keyboard.dismiss()
    }
    releaseModal()
  }, [releaseModal, setContextIsDropdownOpened])

  useEffect(() => {
    const input = inputRef
    return () => {
      input.current?.blur()
    }
  }, [])

  // A sheet relayout can sample the field while it is translated off-screen.
  // Ignore that frame and measure again once layout has settled.
  const measureContainerInWindow = useCallback(
    (onMeasured: (frame: { x: number; y: number; width: number; height: number }) => void) => {
      const windowHeight = window.height
      const requestId = ++measureRequestId.current

      const readFrame = (retriesRemaining: number) => {
        containerRef.current?.measureInWindow((x, y, width, height) => {
          if (requestId !== measureRequestId.current) return

          const frame = { x, y, width, height }
          const isMostlyBelowWindow = y + height / 2 >= windowHeight
          if (isMostlyBelowWindow && retriesRemaining > 0) {
            requestAnimationFrame(() => readFrame(retriesRemaining - 1))
            return
          }
          if (!isMostlyBelowWindow) {
            onMeasured(frame)
          }
        })
      }

      readFrame(1)
    },
    [window.height]
  )

  // Update modal position if the window resizes.
  useEffect(() => {
    if (!focused) return

    measureContainerInWindow(setPosition)
  }, [focused, measureContainerInWindow, window.width])

  // The modal is portaled, so it does not move with the sheet or with an
  // intake question that is sliding out. Track the field and dismiss the copy
  // once that field leaves the screen.
  useEffect(() => {
    if (!modalVisible) return

    let cancelled = false
    let frame = 0

    const track = () => {
      containerRef.current?.measureInWindow((x, y, width, height) => {
        if (cancelled) return

        if (width <= 0 || height <= 0) {
          frame = requestAnimationFrame(track)
          return
        }

        const isOffScreen =
          y + height / 2 >= window.height || y + height <= 0 || x + width <= 0 || x >= window.width

        if (isOffScreen) {
          closeDropdown()
          return
        }

        setPosition((current) => {
          if (
            current &&
            Math.abs(current.x - x) < 0.5 &&
            Math.abs(current.y - y) < 0.5 &&
            Math.abs(current.width - width) < 0.5 &&
            Math.abs(current.height - height) < 0.5
          ) {
            return current
          }
          return { x, y, width, height }
        })
        frame = requestAnimationFrame(track)
      })
    }

    frame = requestAnimationFrame(track)
    return () => {
      cancelled = true
      cancelAnimationFrame(frame)
    }
  }, [closeDropdown, modalVisible, window.height, window.width])

  useEffect(() => {
    if (!readyToOpen || !openRequested) return

    setOpenRequested(false)
    measureContainerInWindow((frame) => {
      setPosition(frame)
      setModalVisible(true)
      setFocused(true)
      setContextIsDropdownOpened(true)
      setInput('')
      setTransitioning(true)
      setOptions([])
    })
  }, [measureContainerInWindow, openRequested, readyToOpen, setContextIsDropdownOpened])

  const handleFocus = () => setOpenRequested(true)

  const handleOnTapOutside = () => {
    setInput(option?.label ?? '')
    closeDropdown()
  }

  const animatedLabelStyle = useAnimatedStyle(() => ({
    opacity: focusedAnim.value > 0 ? 0 : 1,
  }))

  const animatedDropdownLabelStyle = useAnimatedStyle(() => ({
    opacity: focusedAnim.value <= 0 ? 0 : 1,
  }))

  const animatedDropdownContainerStyle = useAnimatedStyle(() => {
    const offset = focusedAnim.value * (positionY - dropdownTargetTop)
    return {
      transform:
        hasContext && Platform.OS !== 'web' && offset > 40 ? [{ translateY: -offset }] : undefined,
    }
  })

  const animatedDropdownStyle = useAnimatedStyle(() => {
    return {
      height: INPUT_HEIGHT + focusedAnim.value * DROPDOWN_HEIGHT,
      borderColor: interpolateColor(
        focusedAnim.value,
        [0, 1],
        [theme.colors.elementsBorder, theme.colors.primaryText]
      ),
      ...(Platform.OS === 'android'
        ? {
            elevation: focusedAnim.value * 2,
          }
        : // Web doesn't support animating the different shadow properties, so we use box shadow instead.
        Platform.OS === 'web'
        ? {
            boxShadow: `rgba(0, 0, 0, ${interpolate(
              focusedAnim.value,
              [0, 1],
              [0, 0.16]
            )}) 0px ${interpolate(focusedAnim.value, [0, 1], [0, 3])}px ${interpolate(
              focusedAnim.value,
              [0, 1],
              [0, 5]
            )}px`,
          }
        : {
            shadowOpacity: interpolate(focusedAnim.value, [0, 1], [0, 0.16]),
            shadowRadius: interpolate(focusedAnim.value, [0, 1], [0, 5]),
            shadowOffset: {
              width: 0,
              height: interpolate(focusedAnim.value, [0, 1], [0, 3]),
            },
          }),
    }
  })

  const onKeyPress = (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    if (Platform.OS === 'web') {
      if (e.nativeEvent.key === 'ArrowDown') {
        setHighlightedIndex((prev) => Math.min(prev + 1, options.length - 1))
        e.preventDefault()
      } else if (e.nativeEvent.key === 'ArrowUp') {
        setHighlightedIndex((prev) => Math.max(prev - 1, 0))
        e.preventDefault()
      } else if (e.nativeEvent.key === 'Enter' && highlightedIndex >= 0) {
        const selected = options[highlightedIndex]
        onChange(selected)
        setInput(selected.label)
        closeDropdown()
        e.preventDefault()
      } else if (e.nativeEvent.key === 'Escape') {
        closeDropdown()
        e.preventDefault()
      }
    }
  }

  const renderItem = ({ item, index }: { item: T; index: number }) => {
    const selected = item.value === option?.value
    return (
      <TouchableHighlight
        underlayColor={theme.colors.contrastElementBg}
        onPress={() => {
          onChange(item)
          setInput(item.label)
          closeDropdown()
        }}
        role="option"
        aria-selected={selected}
      >
        <StyledOption selected={selected} highlighted={index === highlightedIndex}>
          {item.emoji ? <Text>{item.emoji}</Text> : null}
          <View>
            <HighlightedText text={item.label} searchTerm={input} />
            {item.caption && <StyledCaption numberOfLines={1}>{item.caption}</StyledCaption>}
          </View>
        </StyledOption>
      </TouchableHighlight>
    )
  }

  // Values that `renderItem` depends on must be added here.
  const extraData = useMemo(
    () => [highlightedIndex, option, input],
    [highlightedIndex, option, input]
  )

  return (
    <StyledAutocompleteContainer testID={testID}>
      <AutocompleteContainerView testID="AUTOCOMPLETE_CONTAINER" ref={containerRef}>
        {label && (
          <Label
            label={label}
            showRequiredAsterisk={showRequiredAsterisk}
            style={animatedLabelStyle}
          />
        )}
        <TouchableWithoutFeedback testID="AUTOCOMPLETE_VALUE_CONTAINER" onPress={handleFocus}>
          <StyledValueContainer
            style={{ opacity: modalVisible ? 0 : 1 }}
            accessibilityElementsHidden={modalVisible}
            importantForAccessibility={modalVisible ? 'no-hide-descendants' : 'auto'}
          >
            {icon && <StyledIconContainer>{icon}</StyledIconContainer>}
            <StyledValueLabel numberOfLines={1} hasIcon={!!icon}>
              {option?.label ?? placeholder}
            </StyledValueLabel>
          </StyledValueContainer>
        </TouchableWithoutFeedback>
      </AutocompleteContainerView>
      <Infotip
        error={error}
        infotip={infotip}
        isFocused={focused}
        isPersistent={isPersistentInfotip}
      />
      {position != null && (
        <Modal
          visible={modalVisible}
          transparent={true}
          animationType="none"
          onShow={() => inputRef.current?.focus()}
        >
          <TouchableWithoutFeedback onPress={handleOnTapOutside}>
            <StyledDropdownContainer>
              <Animated.View
                style={[
                  {
                    position: 'absolute',
                    top: isIOS() ? Math.max(position.y, minimumDropdownTop) : position.y,
                    left: position.x,
                    width: position.width,
                  },
                  animatedDropdownContainerStyle,
                ]}
              >
                {label && (
                  <Label
                    label={label}
                    showRequiredAsterisk={showRequiredAsterisk}
                    style={animatedDropdownLabelStyle}
                  />
                )}
                {/* On web we need this extra touchable to prevent the dropdown from closing when clicking the input */}
                <TouchableWithoutFeedback onPress={() => {}}>
                  <StyledDropdown style={animatedDropdownStyle}>
                    <StyledDropdownContent
                      // @ts-expect-error RN doesn't support this role.
                      role="listbox"
                    >
                      <StyledInputContainer>
                        {icon && <StyledIconContainer>{icon}</StyledIconContainer>}
                        <StyledInput
                          testID="AUTOCOMPLETE_INPUT"
                          ref={inputRef}
                          value={input}
                          onChangeText={(text) => {
                            setInput(text)
                          }}
                          onKeyPress={onKeyPress}
                          onBlur={() => {
                            if (pendingModalRelease.current) {
                              releaseModal()
                            }
                          }}
                          placeholder={placeholder}
                          placeholderTextColor={error ? theme.colors.error : '#999'}
                          selectionColor={theme.colors.accent}
                          readOnly={transitioning && !focused}
                          maxLength={maxLength}
                          hasIcon={!!icon}
                          style={
                            Platform.OS === 'web'
                              ? {
                                  // @ts-expect-error web only style
                                  outline: 'none',
                                }
                              : {}
                          }
                          role="combobox"
                        />
                        <StyledActivityIndicatorContainer>
                          {loading && <ActivityIndicator color={theme.colors.text} />}
                        </StyledActivityIndicatorContainer>
                      </StyledInputContainer>
                      <FlatList
                        data={options}
                        keyboardShouldPersistTaps="always"
                        showsVerticalScrollIndicator={!transitioning}
                        renderItem={renderItem}
                        style={{
                          height: DROPDOWN_HEIGHT,
                          flexShrink: 0,
                        }}
                        keyExtractor={keyExtractor}
                        extraData={extraData}
                      />
                    </StyledDropdownContent>
                  </StyledDropdown>
                </TouchableWithoutFeedback>
              </Animated.View>
            </StyledDropdownContainer>
          </TouchableWithoutFeedback>
        </Modal>
      )}
    </StyledAutocompleteContainer>
  )
}

export { AutocompleteInput, AutocompleteInputProvider, AutocompleteInputScrollView, wrapScrollView }
